import { AfterViewChecked, ChangeDetectionStrategy, Component, computed, CUSTOM_ELEMENTS_SCHEMA, inject, input } from '@angular/core';
import { COMWidget } from '@/app/models/widget.model';
import { TranslateModule } from '@ngx-translate/core';
import { DsyRowData, DsyTableConf } from '@/app/models/dsy-table.model';
import { ExternalUrlsService } from '@/app/services/external-urls.service';
import { OffersService } from '@/app/services/offers.service';
import { OfferViewModel } from '@/app/models/com/offer.model';
import { toSignal } from '@angular/core/rxjs-interop';
import { catchError, of, switchMap, timer } from 'rxjs';
import { FormatTimeRangePipe } from '@/app/pipes/format-time-range.pipe';
import { DisplayLocalePipe } from '@/app/pipes/display-locale.pipe';
import { LinkButtonComponent } from '@/app/shared/components/link-button/link-button.component';
import { EmptyStateComponent } from '@/app/shared/components/empty-state/empty-state.component';
import { WidgetHeaderComponent } from '@/app/shared/components/widget-header/widget-header.component';
import { TooltipModule } from 'primeng/tooltip';
import { MixPanelEvent } from '@/app/services/mixpanel.service';
import { resizeDsyTableSortIcons } from '@/app/shared/format-styles/dsyTable-style';

@Component({
  selector: 'dr-flex-home-ui-com-component',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './com-offers-widget.component.html',
  styleUrl: './com-offers-widget.component.scss',
  imports: [FormatTimeRangePipe, DisplayLocalePipe, LinkButtonComponent, TranslateModule, EmptyStateComponent, WidgetHeaderComponent, TooltipModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class COMOffersWidgetComponent implements AfterViewChecked {
  widget = input.required<COMWidget>();

  private readonly POLLING_INTERVAL = 15 * 60 * 1000;
  private readonly OFFER_LIMIT = 3;
  private readonly offerService = inject(OffersService);
  readonly mixPanelEvent = MixPanelEvent;

  readonly offers = toSignal<OfferViewModel[] | null>(
    timer(0, this.POLLING_INTERVAL).pipe(switchMap(() => this.offerService.getLatestOffers({ limit: this.OFFER_LIMIT }).pipe(catchError(() => of(null))))),
    { initialValue: null }
  );

  readonly columnsDefinition = [
    {
      key: 'site',
      header: 'FLEX_HOME.COM_WIDGET.OFFERS.COLUMNS.SITE',
      // This function cleanly extracts the data for a cell
      dataAccessor: (offer: OfferViewModel) => offer.siteName[0]?.label || '',
      isTime: false,
      isSortable: false,
    },
    {
      key: 'program',
      header: 'FLEX_HOME.COM_WIDGET.OFFERS.COLUMNS.PROGRAM',
      dataAccessor: (offer: OfferViewModel) => offer.programName[0]?.label || '',
      isTime: false,
      isSortable: false,
    },
    {
      key: 'time',
      header: 'FLEX_HOME.COM_WIDGET.OFFERS.COLUMNS.TIME_PERIOD',
      dataAccessor: (offer: OfferViewModel) => offer.offerStartDttmUtc,
      isTime: true,
      isSortable: false,
    },
    {
      key: 'value',
      header: 'FLEX_HOME.COM_WIDGET.OFFERS.COLUMNS.OFFER',
      dataAccessor: (offer: OfferViewModel) => String(offer.offerValue[0]?.value || ''),
      isTime: false,
      isSortable: true,
    },
  ];

  readonly comUrl = inject(ExternalUrlsService).com;

  // Generate the table data dynamically from the column definitions.
  readonly tableData = computed<DsyRowData[] | null>(() => {
    const currentOffers = this.offers();
    if (!currentOffers) return null;

    return currentOffers.map((offer, index) => ({
      id: `offer-row-${index}`,
      // Create the cells for each row
      cells: Object.fromEntries(
        this.columnsDefinition.map((col) => [
          col.key,
          {
            id: `cell-${index}-${col.key}`,
            property: col.key,
            value: col.dataAccessor(offer),
          },
        ])
      ),
    }));
  });

  // Generate the table configuration dynamically from the same definitions.
  readonly tableConf = computed<DsyTableConf>(() => {
    const rows = this.tableData() ?? [];
    const rowOrder = rows.map((row) => row.id);
    const columnOrder = this.columnsDefinition.map((col) => col.key);

    return {
      styling: {
        table: {
          areRowsSeparated: true,
          density: 'default',
        },
      },
      renderOrder: {
        columns: {
          defaultOrder: columnOrder,
        },
        rows: {
          defaultOrder: rowOrder,
        },
      },
      headerRow: {
        id: 'main-header',
        cells: Object.fromEntries(
          this.columnsDefinition.map((col) => [
            col.key,
            {
              id: `header-${col.key}`,
              property: col.key,
              value: col.header,
              isSortable: col.isSortable,
            },
          ])
        ),
      },
    };
  });

  ngAfterViewChecked() {
    resizeDsyTableSortIcons();
    
  }
}
